/* Obtino PDP for Bosant bedding sets.
 * One controller per [data-obt-pdp] root. Re-initialised safely on Shopify theme-editor section load;
 * every listener is bound through an AbortController so unload/reload never duplicates handlers.
 * Cart: POST /cart/add.js with the main variant + opted-in extras, then asks the Broadcast cart drawer to refresh/open.
 */
(function () {
  'use strict';
  if (window.__obtPdpLoaded) return;
  window.__obtPdpLoaded = true;

  var instances = new Map();
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function fmt(cents, currency) {
    var v = (Number(cents) || 0) / 100;
    var hasOre = Math.round(v * 100) % 100 !== 0;
    var n = new Intl.NumberFormat('nb-NO', { minimumFractionDigits: hasOre ? 2 : 0, maximumFractionDigits: 2 }).format(v);
    return (currency && currency !== 'NOK' ? n + ' ' + currency : n + ' kr');
  }
  function sizeLabel(raw) {
    return String(raw || '').replace(/cm/i, '').trim().replace(/\s*[xX]\s*/, ' x ') + ' cm';
  }
  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  /* ---------------------------------------------------------------- scroll lock */
  var lock = { count: 0, y: 0 };
  function lockScroll() {
    if (lock.count++ > 0) return;
    lock.y = window.scrollY;
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--obt-sbw', sbw + 'px');
    document.documentElement.classList.add('obt-dialog-open');
    if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
  }
  function unlockScroll() {
    if (--lock.count > 0) return;
    lock.count = 0;
    document.documentElement.classList.remove('obt-dialog-open');
    document.body.style.paddingRight = '';
    if (Math.abs(window.scrollY - lock.y) > 1) window.scrollTo({ top: lock.y, behavior: 'instant' });
  }

  /* ================================================================ Controller */
  function Pdp(root) {
    this.root = root;
    this.ac = new AbortController();
    var dataEl = $(root, '[data-obt-data]');
    this.data = dataEl ? JSON.parse(dataEl.textContent) : { variants: [], siblings: [] };
    this.variantsById = {};
    this.variantsByKey = {};
    var self = this;
    this.data.variants.forEach(function (v) { self.variantsById[v.id] = v; self.variantsByKey[v.key] = v; });
    this.variant = null;
    this.busy = false;
    this.observers = [];

    this.initGallery();
    this.initLightbox();
    this.initSheets();
    this.initSelection();
    this.initAccessories();
    this.initSticky();
    this.initStickyColumn();
    this.initColourPreview();
  }

  Pdp.prototype.on = function (el, type, fn, opts) {
    if (!el) return;
    var o = Object.assign({ signal: this.ac.signal }, opts || {});
    el.addEventListener(type, fn, o);
  };

  Pdp.prototype.destroy = function () {
    this.ac.abort();
    (this.observers || []).forEach(function (o) { o.disconnect(); });
    if (this.lightbox && this.lightbox.open) this.lightbox.close();
    document.body.classList.remove('obt-sticky-on');
  };

  /* ---------------------------------------------------------------- gallery */
  Pdp.prototype.initGallery = function () {
    var g = $(this.root, '[data-obt-gallery]');
    if (!g) return;
    var self = this;
    this.g = {
      el: g,
      track: $(g, '[data-obt-track]'),
      slides: $$(g, '[data-obt-slide]'),
      thumbs: $$(g, '[data-obt-thumb]'),
      dots: $$(g, '.obt-gallery__dot'),
      prev: $(g, '[data-obt-prev]'),
      next: $(g, '[data-obt-next]'),
      count: $(g, '[data-obt-count]'),
      live: $(g, '[data-obt-gallery-live]'),
      opens: $$(g, '[data-obt-open]'),
      index: 0
    };

    this.g.thumbs.forEach(function (t) {
      self.on(t, 'click', function () { self.galleryGo(Number(t.dataset.obtThumb), true); });
    });
    this.on(this.g.prev, 'click', function () { self.galleryGo(self.g.index - 1, true); });
    this.on(this.g.next, 'click', function () { self.galleryGo(self.g.index + 1, true); });
    this.on(this.g.track, 'keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); self.galleryGo(self.g.index + 1, true, true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.galleryGo(self.g.index - 1, true, true); }
    });
    this.g.opens.forEach(function (b) {
      self.on(b, 'click', function () { self.openLightbox(Number(b.dataset.obtOpen), b); });
    });

    // user swipes on touch: observe which slide is mostly visible (no scroll listeners)
    if ('IntersectionObserver' in window && this.g.slides.length > 1) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting && en.intersectionRatio >= 0.6) {
            var i = Number(en.target.dataset.obtSlide);
            if (i !== self.g.index) self.galleryUpdate(i, false);
          }
        });
      }, { root: this.g.track, threshold: [0.6] });
      this.g.slides.forEach(function (s) { io.observe(s); });
      this.observers.push(io);
    }
  };

  Pdp.prototype.galleryGo = function (i, smooth, focusSlide) {
    var g = this.g; if (!g || !g.slides.length) return;
    i = Math.max(0, Math.min(g.slides.length - 1, i));
    var target = g.slides[i];
    g.track.scrollTo({ left: target.offsetLeft, behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto' });
    this.galleryUpdate(i, true);
    if (focusSlide) { var b = $(target, '[data-obt-open]'); if (b) b.focus({ preventScroll: true }); }
  };

  Pdp.prototype.galleryUpdate = function (i, announce) {
    var g = this.g; g.index = i;
    g.thumbs.forEach(function (t, n) { if (n === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
    var active = g.thumbs[i];
    if (active && active.parentElement) {
      var p = active.parentElement;
      var l = active.offsetLeft - p.offsetLeft;
      if (l < p.scrollLeft || l + active.offsetWidth > p.scrollLeft + p.clientWidth) {
        p.scrollTo({ left: l - 8, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    }
    g.dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
    if (g.count) g.count.textContent = String(i + 1);
    if (g.prev) g.prev.disabled = i === 0;
    if (g.next) g.next.disabled = i === g.slides.length - 1;
    if (announce && g.live) g.live.textContent = 'Bilde ' + (i + 1) + ' av ' + g.slides.length;
  };

  /* ---------------------------------------------------------------- lightbox */
  Pdp.prototype.initLightbox = function () {
    var d = $(this.root, '[data-obt-lightbox]');
    if (!d || !this.g) return;
    var self = this;
    this.lightbox = d;
    this.lb = {
      img: $(d, '[data-obt-lb-img]'),
      stage: $(d, '[data-obt-lb-stage]'),
      count: $(d, '[data-obt-lb-count]'),
      live: $(d, '[data-obt-lb-live]'),
      zoomBtn: $(d, '[data-obt-lb-zoom]'),
      hint: $(d, '[data-obt-lb-hint]'),
      index: 0, zoom: 1, tx: 0, ty: 0, trigger: null, pointers: new Map(), start: null, moved: false
    };
    // items from the gallery buttons: one authoritative list
    this.lbItems = this.g.opens.map(function (b) {
      var img = b.querySelector('img');
      return { large: b.dataset.large, full: b.dataset.full, w: Number(b.dataset.w), h: Number(b.dataset.h), alt: img ? img.alt : '' };
    });

    this.on($(d, '[data-obt-lb-close]'), 'click', function () { self.closeLightbox(); });
    this.on($(d, '[data-obt-lb-prev]'), 'click', function () { self.lbGo(self.lb.index - 1); });
    this.on($(d, '[data-obt-lb-next]'), 'click', function () { self.lbGo(self.lb.index + 1); });
    this.on(this.lb.zoomBtn, 'click', function () { self.lbZoom(self.lb.zoom > 1 ? 1 : 2.5); });
    this.on(d, 'cancel', function (e) { e.preventDefault(); self.closeLightbox(); }); // Escape
    this.on(d, 'close', function () { self.afterLightboxClose(); });
    this.on(d, 'keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); self.lbGo(self.lb.index + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); self.lbGo(self.lb.index - 1); }
      else if (e.key === '+' || e.key === '=') { self.lbZoom(2.5); }
      else if (e.key === '-') { self.lbZoom(1); }
    });
    // click on the dark area around the image closes; clicks on the image zoom
    this.on(d, 'click', function (e) {
      if (e.target === d) self.closeLightbox();
    });

    var st = this.lb.stage;
    this.on(st, 'pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      st.setPointerCapture(e.pointerId);
      self.lb.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      self.lb.start = { x: e.clientX, y: e.clientY, tx: self.lb.tx, ty: self.lb.ty, t: Date.now(), target: e.target };
      self.lb.moved = false;
    });
    this.on(st, 'pointermove', function (e) {
      if (!self.lb.pointers.has(e.pointerId) || !self.lb.start) return;
      var dx = e.clientX - self.lb.start.x, dy = e.clientY - self.lb.start.y;
      if (Math.abs(dx) + Math.abs(dy) > 6) self.lb.moved = true;
      if (self.lb.zoom > 1) {
        d.classList.add('is-panning');
        self.lb.tx = self.lb.start.tx + dx; self.lb.ty = self.lb.start.ty + dy;
        self.lbApply(false);
      }
    });
    var end = function (e) {
      if (!self.lb.pointers.has(e.pointerId)) return;
      self.lb.pointers.delete(e.pointerId);
      d.classList.remove('is-panning');
      var s = self.lb.start; self.lb.start = null;
      if (!s || e.type === 'pointercancel') return;
      var dx = e.clientX - s.x, dy = e.clientY - s.y;
      if (self.lb.zoom === 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        self.lbGo(self.lb.index + (dx < 0 ? 1 : -1));
      } else if (!self.lb.moved && s.target === self.lb.img) {
        if (self.lb.zoom > 1) self.lbZoom(1); else self.lbZoom(2.5, e.clientX, e.clientY);
      } else if (!self.lb.moved && s.target === st && self.lb.zoom === 1) {
        self.closeLightbox();
      }
      if (self.lb.zoom > 1) self.lbClamp(true);
    };
    this.on(st, 'pointerup', end);
    this.on(st, 'pointercancel', end);
    this.on(st, 'wheel', function (e) {
      if (!e.ctrlKey && self.lb.zoom === 1) return;
      e.preventDefault();
      if (self.lb.zoom > 1 && !e.ctrlKey) { self.lb.tx -= e.deltaX; self.lb.ty -= e.deltaY; self.lbClamp(false); }
      else self.lbZoom(e.deltaY < 0 ? 2.5 : 1, e.clientX, e.clientY);
    }, { passive: false });
    this.on(window, 'resize', function () { if (d.open) { self.lbZoom(1); } });
  };

  Pdp.prototype.openLightbox = function (i, trigger) {
    var d = this.lightbox; if (!d || !this.lbItems.length) return;
    if (d.open) return;
    this.lb.trigger = trigger || document.activeElement;
    this.lb.scrollY = window.scrollY;
    this.lbShow(i);
    lockScroll();
    try { d.showModal(); } catch (err) { d.setAttribute('open', ''); }
    var closeBtn = $(d, '[data-obt-lb-close]');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  };

  Pdp.prototype.closeLightbox = function () {
    var d = this.lightbox; if (!d || !d.open) return;
    d.close();
  };

  Pdp.prototype.afterLightboxClose = function () {
    this.lbZoom(1);
    unlockScroll();
    // keep the page gallery on the image the customer ended on, then return focus there
    var i = this.lb.index;
    this.galleryGo(i, false);
    var target = this.g.opens[i] || this.lb.trigger;
    if (target && document.contains(target)) target.focus({ preventScroll: true });
    if (typeof this.lb.scrollY === 'number' && Math.abs(window.scrollY - this.lb.scrollY) > 1) window.scrollTo({ top: this.lb.scrollY, behavior: 'instant' });
  };

  Pdp.prototype.lbShow = function (i) {
    var n = this.lbItems.length;
    i = (i + n) % n; // wraps in the viewer so the last image is never a dead end
    this.lb.index = i;
    var item = this.lbItems[i];
    this.lbZoom(1, null, null, true);
    var img = this.lb.img;
    img.removeAttribute('src');
    img.width = item.w || 1600; img.height = item.h || 2000;
    img.src = item.large;
    img.alt = item.alt || '';
    img.dataset.full = item.full;
    img.dataset.loadedFull = '';
    this.lb.count.textContent = String(i + 1);
    this.lb.live.textContent = 'Bilde ' + (i + 1) + ' av ' + n + (item.alt ? ': ' + item.alt : '');
    // warm neighbours
    [i + 1, i - 1].forEach(function (k) { var it = this.lbItems[(k + n) % n]; if (it) { var p = new Image(); p.src = it.large; } }, this);
  };

  Pdp.prototype.lbGo = function (i) { if (this.lbItems.length > 1) this.lbShow(i); };

  Pdp.prototype.lbZoom = function (z, cx, cy, instant) {
    var lb = this.lb; if (!lb) return;
    var d = this.lightbox, img = lb.img;
    if (z > 1) {
      if (!img.dataset.loadedFull && img.dataset.full) { img.dataset.loadedFull = '1'; img.src = img.dataset.full; }
      var r = img.getBoundingClientRect();
      // current untransformed box
      var bw = r.width / lb.zoom, bh = r.height / lb.zoom;
      var bx = r.left - lb.tx, by = r.top - lb.ty;
      var px = (cx == null ? bx + bw / 2 : cx) - bx, py = (cy == null ? by + bh / 2 : cy) - by;
      lb.zoom = z;
      lb.tx = -(px * (z - 1)); lb.ty = -(py * (z - 1));
    } else {
      lb.zoom = 1; lb.tx = 0; lb.ty = 0;
    }
    d.classList.toggle('is-zoomed', lb.zoom > 1);
    if (lb.zoomBtn) { lb.zoomBtn.setAttribute('aria-pressed', String(lb.zoom > 1)); lb.zoomBtn.setAttribute('aria-label', lb.zoom > 1 ? 'Zoom ut' : 'Zoom inn'); }
    if (lb.hint) lb.hint.textContent = lb.zoom > 1 ? 'Dra for å flytte. Klikk eller trykk for å zoome ut.' : 'Klikk eller trykk på bildet for å zoome.';
    this.lbClamp(!instant);
  };

  Pdp.prototype.lbClamp = function (animate) {
    var lb = this.lb, img = lb.img;
    if (lb.zoom > 1) {
      var w = img.offsetWidth, h = img.offsetHeight, z = lb.zoom;
      var st = lb.stage.getBoundingClientRect();
      var ox = img.offsetLeft, oy = img.offsetTop; // position inside stage
      // keep the image covering the stage where possible
      var minX = Math.min(0, st.width - (ox + w * z)), maxX = Math.max(0, -ox);
      var minY = Math.min(0, st.height - (oy + h * z)), maxY = Math.max(0, -oy);
      if (w * z < st.width) { minX = maxX = (st.width - w * z) / 2 - ox; }
      if (h * z < st.height) { minY = maxY = (st.height - h * z) / 2 - oy; }
      lb.tx = Math.min(maxX, Math.max(minX, lb.tx));
      lb.ty = Math.min(maxY, Math.max(minY, lb.ty));
    }
    this.lbApply(animate);
  };

  Pdp.prototype.lbApply = function (animate) {
    var lb = this.lb, d = this.lightbox;
    d.classList.toggle('is-animating', !!animate && !reduceMotion.matches);
    lb.img.style.transform = lb.zoom > 1 ? 'translate(' + lb.tx + 'px,' + lb.ty + 'px) scale(' + lb.zoom + ')' : '';
  };

  /* ---------------------------------------------------------------- sheets (size guides) */
  Pdp.prototype.initSheets = function () {
    var self = this;
    $$(this.root, '[data-obt-open-sheet]').forEach(function (btn) {
      self.on(btn, 'click', function () {
        var d = document.getElementById(btn.dataset.obtOpenSheet);
        if (!d || d.open) return;
        d._obtTrigger = btn;
        self.markGuideRow();
        lockScroll();
        d.showModal();
      });
    });
    $$(this.root, 'dialog.obt-sheet').forEach(function (d) {
      self.on($(d, '[data-obt-close-sheet]'), 'click', function () { d.close(); });
      self.on(d, 'click', function (e) { if (e.target === d) d.close(); });
      self.on(d, 'close', function () {
        unlockScroll();
        if (d._obtTrigger) d._obtTrigger.focus({ preventScroll: true });
      });
    });
  };

  Pdp.prototype.markGuideRow = function () {
    var id = this.variant ? String(this.variant.id) : '';
    $$(this.root, '[data-obt-guide-row]').forEach(function (tr) { tr.classList.toggle('is-selected', tr.dataset.obtGuideRow === id); });
  };

  /* ---------------------------------------------------------------- selection */
  Pdp.prototype.initSelection = function () {
    var self = this;
    this.form = $(this.root, '[data-obt-form]');
    this.sizeInputs = $$(this.root, '[data-obt-size-input]');
    this.sizeGroup = $(this.root, '[data-obt-sizes]');
    this.sizeError = $(this.root, '[data-obt-size-error]');
    this.addBtn = $(this.root, '[data-obt-add]');
    this.addLabel = $(this.root, '[data-obt-add-label]');
    this.idInput = $(this.root, '[data-obt-id]');
    this.optCount = (this.data.optionNames || []).length;
    this.hasOpts = this.sizeInputs.length > 0;
    this.sel = [];

    this.sizeInputs.forEach(function (inp) {
      self.on(inp, 'change', function () { self.sel[Number(inp.dataset.obtOpt)] = inp.value; self.resolve(true); });
    });
    // dropdowns are the visible control; the hidden radios keep the state
    this.selects = $$(this.root, '[data-obt-select]');
    this.selects.forEach(function (sel) {
      self.on(sel, 'change', function () {
        var oi = Number(sel.dataset.obtSelect);
        self.sizeInputs.forEach(function (i) { if (Number(i.dataset.obtOpt) === oi) i.checked = i.value === sel.value; });
        self.sel[oi] = sel.value || null;
        self.resolve(true);
      });
    });
    this.on(this.form, 'submit', function (e) { e.preventDefault(); self.addToCart(); });

    var params = new URLSearchParams(location.search);
    var fromUrl = Number(params.get('variant'));
    if (fromUrl && this.variantsById[fromUrl]) {
      this.selectVariantOptions(this.variantsById[fromUrl]);
      this.resolve(false);
    } else if (this.hasOpts) {
      // restored radios (bfcache / back button) or nothing
      this.readChecked();
      this.resolve(false);
    } else if (this.idInput && this.idInput.value) {
      this.setVariant(Number(this.idInput.value), false);
    } else {
      this.render();
    }

    // arrived from another colour whose size does not exist / is not available here
    var wanted = params.get('obt_size');
    if (wanted && !fromUrl) {
      var key = wanted.replace(/\s|cm/gi, '').toLowerCase();
      var v = this.data.variants.filter(function (x) { return x.tkey === key || x.key === key; })[0];
      var note = $(this.root, '[data-obt-colour-notice]');
      var label = /^\d/.test(wanted) ? sizeLabel(wanted) : wanted;
      var msg;
      if (!v) msg = label + ' finnes ikke i denne fargen. Velg en annen størrelse.';
      else if (!v.available) msg = label + ' er utsolgt i denne fargen. Velg en annen størrelse.';
      if (msg && note) { note.textContent = msg; note.hidden = false; }
      if (v && v.available) { this.selectVariantOptions(v); this.resolve(true); }
    }

    this.on(window, 'pageshow', function (e) { if (e.persisted) { self.readChecked(); self.resolve(false); } });
  };

  Pdp.prototype.readChecked = function () {
    var self = this; this.sel = [];
    this.sizeInputs.forEach(function (i) { if (i.checked) self.sel[Number(i.dataset.obtOpt)] = i.value; });
  };

  Pdp.prototype.selectVariantOptions = function (v) {
    var self = this;
    this.sel = v.options.slice();
    this.sizeInputs.forEach(function (i) { i.checked = self.sel[Number(i.dataset.obtOpt)] === i.value; });
  };

  Pdp.prototype.resolve = function (updateUrl) {
    var sel = this.sel, n = this.optCount;
    var complete = true;
    for (var k = 0; k < n; k++) if (sel[k] == null) complete = false;
    var v = null;
    if (complete) v = this.data.variants.filter(function (x) { return x.options.every(function (o, idx) { return o === sel[idx]; }); })[0] || null;
    this.updateOptionStates();
    if (complete && !v) {
      // combination does not exist
      this.variant = null;
      this.render();
      var err = $(this.root, '[data-obt-size-errtext]');
      if (err) err.textContent = 'Denne kombinasjonen finnes ikke. Velg et annet alternativ.';
      if (this.sizeError) this.sizeError.hidden = false;
      return;
    }
    this.setVariant(v ? v.id : null, updateUrl);
  };

  // mark values that are sold out / impossible given the other chosen options
  Pdp.prototype.updateOptionStates = function () {
    var self = this, variants = this.data.variants, sel = this.sel;
    this.sizeInputs.forEach(function (inp) {
      var oi = Number(inp.dataset.obtOpt), val = inp.value;
      var matches = variants.filter(function (v) {
        if (v.options[oi] !== val) return false;
        for (var k = 0; k < v.options.length; k++) { if (k !== oi && sel[k] != null && v.options[k] !== sel[k]) return false; }
        return true;
      });
      var available = matches.some(function (v) { return v.available; });
      var label = inp.closest('[data-obt-optlabel]');
      if (!label) return;
      label.classList.toggle('is-soldout', matches.length > 0 && !available);
      var opt = self.selects && self.selects.length ? $$(self.root, '[data-obt-select="' + oi + '"] option').filter(function (o) { return o.value === val; })[0] : null;
      if (opt) {
        var base = opt.getAttribute('data-base') || val;
        var pre = self.optCount === 1 && /forhåndsbestilling/.test(opt.textContent) ? ' – forhåndsbestilling' : '';
        opt.textContent = base + (matches.length === 0 ? ' – ikke tilgjengelig' : (!available ? ' – utsolgt' : pre));
        opt.disabled = matches.length === 0;
      }
      label.classList.toggle('is-impossible', matches.length === 0);
      if (self.optCount > 1) {
        var flag = $(label, '[data-obt-optflag]');
        if (flag) flag.textContent = matches.length === 0 ? 'Ikke tilgjengelig' : (!available ? 'Utsolgt' : '');
      }
    });
    (this.selects || []).forEach(function (s) { var oi = Number(s.dataset.obtSelect); s.value = sel[oi] != null ? sel[oi] : ''; });
    (this.data.optionNames || []).forEach(function (name, oi) {
      var el = $(self.root, '[data-obt-optvalue="' + oi + '"]');
      if (el) el.textContent = sel[oi] ? (/^\d/.test(sel[oi]) ? sizeLabel(sel[oi]) : sel[oi]) : '';
    });
  };

  Pdp.prototype.setVariant = function (id, updateUrl) {
    var v = id ? (this.variantsById[id] || null) : null;
    this.variant = v;
    if (this.idInput) this.idInput.value = v ? v.id : '';
    if (v) {
      if (this.sizeGroup) this.sizeGroup.classList.remove('is-invalid');
      if (this.sizeError) this.sizeError.hidden = true;
      var note = $(this.root, '[data-obt-colour-notice]');
      if (note && updateUrl) note.hidden = true;
      if (updateUrl && this.hasOpts) {
        var url = new URL(location.href);
        url.searchParams.set('variant', v.id);
        url.searchParams.delete('obt_size');
        history.replaceState(history.state, '', url.toString());
      }
    }
    this.render();
    document.dispatchEvent(new CustomEvent('obt:variant-change', { detail: { variant: v, productId: this.data.productId, data: this.data } }));
  };

  Pdp.prototype.variantLabel = function (v) {
    if (!v) return '';
    return v.options.map(function (o) { return /^\d/.test(o) && /x/i.test(o) ? sizeLabel(o) : o; }).join(' / ');
  };

  Pdp.prototype.firstMissing = function () {
    for (var k = 0; k < this.optCount; k++) if (this.sel[k] == null) return k;
    return 0;
  };

  Pdp.prototype.isDouble = function (v) { return !!v && this.data.doubleSizes.indexOf(v.key) !== -1; };

  Pdp.prototype.extrasTotal = function () {
    var sum = 0, count = 0;
    (this.extras || []).forEach(function (x) { if (x.checked && x.variantId) { sum += x.price; count++; } });
    return { sum: sum, count: count };
  };

  Pdp.prototype.render = function () {
    var v = this.variant, d = this.data, cur = d.currency;
    var root = this.root;

    // price
    var priceEl = $(root, '[data-obt-price]'), cmp = $(root, '[data-obt-compare]');
    if (v && priceEl) {
      priceEl.textContent = fmt(v.price, cur);
      if (cmp) { if (v.compare > v.price) { cmp.textContent = fmt(v.compare, cur); cmp.hidden = false; } else { cmp.hidden = true; cmp.textContent = ''; } }
      var pctEl = $(root, '[data-obt-pct]');
      if (pctEl) { if (v.compare > v.price) { pctEl.textContent = '\u2212' + Math.round((v.compare - v.price) * 100 / v.compare) + '%'; pctEl.hidden = false; } else { pctEl.hidden = true; } }
    }

    // contents
    var duvet = $(root, '[data-obt-contents-duvet]'), pillow = $(root, '[data-obt-contents-pillow]');
    if (duvet) duvet.textContent = v ? '1 dynetrekk, ' + sizeLabel(v.size) : '1 dynetrekk';
    var line = $(root, '[data-obt-contents-line]');
    if (line) {
      var tpl = line.dataset.template || '';
      line.textContent = v && v.title !== 'Default Title' ? tpl.replace('{size}', this.variantLabel(v)) : tpl.replace(', {size}', '').replace('{size}', '');
    }
    if (pillow) pillow.textContent = v ? (this.isDouble(v) ? '2 putetrekk, ' : '1 putetrekk, ') + d.pillowSize : '1 putetrekk (' + d.pillowSize + '), 2 med dobbeltdyne';

    // stock
    var stock = $(root, '[data-obt-stock]');
    var state = !v ? '' : !v.available ? 'out' : v.preorder || !v.inStock ? 'pre' : 'in';
    if (stock) {
      stock.dataset.state = state;
      stock.textContent = !v ? '' : state === 'out' ? (this.hasOpts ? this.variantLabel(v) + ' er utsolgt.' : 'Utsolgt.') : state === 'pre' ? d.preorderText : d.inStockText;
    }

    // button
    var optName = ((this.data.optionNames || [])[this.firstMissing ? this.firstMissing() : 0] || 'størrelse').toLowerCase();
    if (optName.length > 16) optName = 'størrelse';
    var label = !v && this.hasOpts ? 'Velg ' + optName : state === 'out' ? 'Utsolgt' : state === 'pre' ? 'Forhåndsbestill' : 'Legg i handlekurven';
    if (this.addLabel && !this.busy) this.addLabel.textContent = label;
    if (this.addBtn) {
      if (state === 'out') this.addBtn.setAttribute('aria-disabled', 'true'); else this.addBtn.removeAttribute('aria-disabled');
    }
    this.buttonLabel = label;

    // summary for extras
    var ex = this.extrasTotal();
    var summary = $(root, '[data-obt-summary]');
    var accTotal = $(root, '[data-obt-acc-total]'), accVal = $(root, '[data-obt-acc-total-value]');
    if (ex.count > 0) {
      var base = v ? v.price : 0;
      var text = v ? 'Sengesett + ' + ex.count + (ex.count === 1 ? ' tillegg' : ' tillegg') + ': <strong>' + fmt(base + ex.sum, cur) + '</strong>'
                   : ex.count + (ex.count === 1 ? ' tillegg valgt' : ' tillegg valgt') + ' (' + fmt(ex.sum, cur) + '). Velg størrelse på sengesettet.';
      if (summary) { summary.innerHTML = text; summary.hidden = false; }
      if (accTotal && accVal) { accVal.textContent = v ? fmt(base + ex.sum, cur) : fmt(ex.sum, cur) + ' + sengesett'; accTotal.hidden = false; }
    } else {
      if (summary) { summary.hidden = true; summary.textContent = ''; }
      if (accTotal) accTotal.hidden = true;
    }

    // sticky bar
    var meta = $(root, '[data-obt-sticky-meta]'), sbtn = $(root, '[data-obt-sticky-btn]');
    if (meta) meta.textContent = v ? (this.hasOpts ? this.variantLabel(v) + ', ' : '') + fmt(v.price + ex.sum, cur) + (ex.count ? ' med tillegg' : '') : 'Velg ' + optName + ' for pris';
    if (sbtn) {
      sbtn.textContent = label === 'Legg i handlekurven' ? 'Legg i handlekurven' : label;
      if (state === 'out') sbtn.setAttribute('aria-disabled', 'true'); else sbtn.removeAttribute('aria-disabled');
    }

    // colour links carry the chosen size when that combination exists and is available
    var sibs = d.siblings || [];
    var self = this;
    sibs.forEach(function (s) {
      var a = root.querySelector('[data-obt-swatch="' + s.id + '"]');
      if (!a || s.id === d.productId) return;
      if (!v) { a.href = s.url; return; }
      var m = s.sizes[v.tkey];
      a.href = m && m.available ? s.url + '?variant=' + m.id : s.url + '?obt_size=' + encodeURIComponent(v.tkey);
    });

    this.markGuideRow();
  };

  /* ---------------------------------------------------------------- accessories */
  Pdp.prototype.initAccessories = function () {
    var self = this;
    this.extras = $$(this.root, '[data-obt-acc-item]').map(function (li) {
      var sel = $(li, '[data-obt-acc-variant]');
      var tog = $(li, '[data-obt-acc-toggle]');
      var err = $(li, '[data-obt-acc-error]');
      var priceEl = $(li, '[data-obt-acc-price]');
      var x = { el: li, sel: sel, tog: tog, err: err, checked: false, variantId: null, price: 0, title: li.dataset.title };
      var read = function () {
        if (!sel) return;
        var opt = sel.tagName === 'SELECT' ? sel.options[sel.selectedIndex] : sel;
        x.variantId = sel.value ? Number(sel.value) : null;
        x.price = x.variantId ? Number(opt.dataset.price || (sel.dataset && sel.dataset.price) || 0) : 0;
        if (x.variantId && priceEl && sel.tagName === 'SELECT') priceEl.textContent = fmt(x.price, self.data.currency);
        var wasEl = $(li, '[data-obt-acc-was]');
        if (x.variantId && wasEl && sel.tagName === 'SELECT') {
          var cmp = Number(opt.dataset.compare || 0);
          wasEl.hidden = !(cmp > x.price);
          if (cmp > x.price) wasEl.innerHTML = '<span class="obt-sr">Før </span>' + fmt(cmp, self.data.currency);
        }
      };
      read();
      self.on(sel, 'change', function () {
        read();
        if (x.variantId) { err.hidden = true; sel.closest('.obt-select') && sel.closest('.obt-select').classList.remove('is-invalid'); }
        self.render();
      });
      self.on(tog, 'change', function () {
        x.checked = tog.checked;
        if (x.checked && !x.variantId) {
          err.hidden = false;
          var wrap = sel && sel.closest('.obt-select'); if (wrap) wrap.classList.add('is-invalid');
          if (sel && sel.focus) sel.focus();
        } else { err.hidden = true; }
        self.render();
      });
      // browser may restore checkbox state on back navigation
      x.checked = !!(tog && tog.checked);
      return x;
    });
    this.render();
  };

  /* ---------------------------------------------------------------- add to cart */
  Pdp.prototype.addToCart = function () {
    var self = this, v = this.variant;
    var errEl = $(this.root, '[data-obt-form-error]');
    if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
    if (this.busy) return;

    if (!v) {
      if (this.sizeGroup) {
        this.sizeGroup.classList.add('is-invalid');
        this.sizeError.hidden = false;
        this.sizeGroup.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
        var miss = String(this.firstMissing());
        var group = this.sizeInputs.filter(function (i) { return i.dataset.obtOpt === miss; });
        var first = group.filter(function (i) { return !i.closest('.is-soldout'); })[0] || group[0] || this.sizeInputs[0];
        first = $(this.root || document, '[data-obt-select="' + miss + '"]') || first; if (first) first.focus({ preventScroll: true });
      }
      return;
    }
    if (!v.available) return;

    var pendingExtra = (this.extras || []).filter(function (x) { return x.checked && !x.variantId; });
    if (pendingExtra.length) {
      pendingExtra[0].err.hidden = false;
      pendingExtra[0].sel.focus();
      if (errEl) { errEl.textContent = 'Velg størrelse på ' + pendingExtra[0].title + ', eller fjern haken.'; errEl.hidden = false; }
      return;
    }

    var props = {};
    var colourProp = $(this.root, '[data-obt-colour-prop]');
    if (colourProp) props.Farge = colourProp.value;
    var items = [{ id: v.id, quantity: 1, properties: props }];
    (this.extras || []).forEach(function (x) { if (x.checked && x.variantId) items.push({ id: x.variantId, quantity: 1 }); });

    this.busy = true;
    this.setBusy(true);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, 15000);

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items }),
      signal: ctrl.signal
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) { return { ok: r.ok, body: j }; });
    }).then(function (res) {
      if (!res.ok) {
        var msg = (res.body && (res.body.description || res.body.message)) || 'Kunne ikke legge i handlekurven.';
        throw new Error(msg);
      }
      self.done(items.length);
    }).catch(function (e) {
      var msg = e && e.name === 'AbortError' ? 'Det tok for lang tid å få svar. Sjekk nettforbindelsen og prøv igjen.' : (e && e.message && e.message !== 'Failed to fetch' ? e.message : 'Noe gikk galt. Sjekk nettforbindelsen og prøv igjen.');
      if (errEl) { errEl.textContent = msg; errEl.hidden = false; }
    }).finally(function () {
      clearTimeout(timer);
      self.busy = false;
      self.setBusy(false);
    });
  };

  Pdp.prototype.setBusy = function (on) {
    [this.addBtn, $(this.root, '[data-obt-sticky-btn]')].forEach(function (b) {
      if (!b) return;
      b.classList.toggle('is-loading', on);
      if (on) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy');
    });
    if (!on) this.render();
  };

  Pdp.prototype.done = function (count) {
    var self = this;
    var btn = this.addBtn;
    // reset extras so a second click does not silently re-add them
    (this.extras || []).forEach(function (x) { if (x.tog) x.tog.checked = false; x.checked = false; });
    if (btn) {
      btn.classList.add('is-done');
      this.addLabel.textContent = count > 1 ? 'Lagt i handlekurven (' + count + ' varer)' : 'Lagt i handlekurven';
      setTimeout(function () { btn.classList.remove('is-done'); self.render(); }, 2200);
    }
    var live = $(this.root, '[data-obt-cart-live]');
    if (live) live.textContent = count > 1 ? count + ' varer er lagt i handlekurven.' : 'Varen er lagt i handlekurven.';
    var cartType = window.theme && window.theme.settings && window.theme.settings.cartType;
    var hasDrawer = document.querySelector('cart-drawer, [data-cart-drawer]');
    if (cartType === 'drawer' || hasDrawer) {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      setTimeout(function () { document.dispatchEvent(new CustomEvent('theme:cart-drawer:show', { bubbles: true })); }, 250);
    } else {
      window.location.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart';
    }
  };

  /* ---------------------------------------------------------------- mobile sticky bar */
  Pdp.prototype.initSticky = function () {
    var bar = $(this.root, '[data-obt-sticky]');
    if (!bar || !this.addBtn || !('IntersectionObserver' in window)) return;
    var self = this;
    var mq = window.matchMedia('(max-width: 989px)');
    var show = function (on) {
      on = on && mq.matches;
      bar.classList.toggle('is-visible', on);
      bar.setAttribute('aria-hidden', String(!on));
      if (on) bar.removeAttribute('inert'); else bar.setAttribute('inert', '');
      document.body.classList.toggle('obt-sticky-on', on);
    };
    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      // only after the main button has scrolled above the viewport
      show(!e.isIntersecting && e.boundingClientRect.top < 0);
    });
    io.observe(this.addBtn);
    this.observers.push(io);
    this.on(mq, 'change', function () { if (!mq.matches) show(false); });

    this.on($(bar, '[data-obt-sticky-btn]'), 'click', function () {
      if (!self.variant && self.sizeGroup) {
        self.sizeGroup.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
        var miss = String(self.firstMissing());
        var first = self.sizeInputs.filter(function (i) { return i.dataset.obtOpt === miss; })[0] || self.sizeInputs[0];
        first = $(this.root || document, '[data-obt-select="' + miss + '"]') || first; if (first) first.focus({ preventScroll: true });
        self.sizeGroup.classList.add('is-invalid'); self.sizeError.hidden = false;
        return;
      }
      if (self.variant && !self.variant.available) return;
      self.addToCart();
    });
  };

  /* ---------------------------------------------------------------- desktop sticky gallery that never hides content */
  Pdp.prototype.initStickyColumn = function () {
    var col = $(this.root, '[data-obt-gallery]');
    if (!col || !('ResizeObserver' in window)) return;
    var root = this.root;
    var set = function () {
      var gap = 24;
      var top = Math.min(gap, window.innerHeight - col.offsetHeight - gap);
      root.style.setProperty('--obt-sticky-top', top + 'px');
    };
    var ro = new ResizeObserver(set);
    ro.observe(col);
    this.observers.push(ro);
    this.on(window, 'resize', set);
    set();
  };

  /* ---------------------------------------------------------------- colour name preview on hover/focus */
  Pdp.prototype.initColourPreview = function () {
    var name = $(this.root, '[data-obt-colour-name]');
    if (!name) return;
    var self = this;
    $$(this.root, '[data-obt-swatch]').forEach(function (a) {
      var show = function () { name.textContent = a.getAttribute('aria-label'); };
      var reset = function () { name.textContent = name.dataset.current; };
      self.on(a, 'mouseenter', show); self.on(a, 'focus', show);
      self.on(a, 'mouseleave', reset); self.on(a, 'blur', reset);
    });
  };

  /* ================================================================ boot */
  function boot(scope) {
    $$(scope || document, '[data-obt-pdp]').forEach(function (el) {
      if (instances.has(el)) return;
      instances.set(el, new Pdp(el));
    });
  }
  function teardown(scope) {
    instances.forEach(function (inst, el) {
      if (!scope || scope.contains(el)) { inst.destroy(); instances.delete(el); }
    });
  }

  /* ================================================================ colour swap without page reload
     Colours are separate products. Clicking a swatch fetches only the product section
     (Section Rendering API), swaps it in place and updates the URL, like the old theme did. */
  var swapping = false;
  var shown = location.pathname + location.search;
  function swapTo(url, sec, push) {
    if (swapping) return;
    swapping = true;
    var id = sec.id.replace('shopify-section-', '');
    var u = new URL(url, location.origin);
    u.searchParams.set('section_id', id);
    sec.classList.add('obt-swapping');
    fetch(u.toString(), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById(sec.id) || doc.querySelector('.shopify-section');
        if (!fresh) throw new Error('no section');
        teardown(sec);
        sec.innerHTML = fresh.innerHTML;
        boot(sec);
        try {
          var ld = sec.querySelector('script[type="application/ld+json"]');
          var name = ld && JSON.parse(ld.textContent).name;
          if (name) document.title = name + ' – Bosant';
        } catch (e) {}
        if (push) history.pushState({ obtSwap: true }, '', url);
        var nu = new URL(url, location.origin); shown = nu.pathname + nu.search;
        document.dispatchEvent(new CustomEvent('obt:product-swapped', { detail: { url: url } }));
      })
      .catch(function () { location.href = url; })
      .then(function () { swapping = false; sec.classList.remove('obt-swapping'); });
  }
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[data-obt-swatch]');
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var sec = a.closest('.shopify-section');
    if (!sec || !window.fetch || !window.DOMParser) return;
    e.preventDefault();
    if (a.classList.contains('is-current')) return;
    swapTo(a.href, sec, true);
  });
  window.addEventListener('popstate', function () {
    var sec = document.querySelector('[data-obt-pdp]');
    sec = sec && sec.closest('.shopify-section');
    if (location.pathname + location.search === shown) return;
    if (sec && /\/products\//.test(location.pathname)) swapTo(location.href, sec, false);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });

  /* lower-page sections that mirror the selection (contents table, final bar) */
  // Mix & Match campaign price follows the chosen variant
  document.addEventListener('obt:variant-change', function (e) {
    var v = e.detail && e.detail.variant; var box = document.querySelector('[data-obt-mm]');
    if (!v || !box) return;
    var amt = box.querySelector('[data-obt-mm-amount]'); var keep = Number(box.dataset.keep || 60);
    var onSale = v.compare_at_price && v.compare_at_price > v.price;
    box.hidden = onSale; var off = document.querySelector('[data-obt-mm-offer]'); if (off) off.hidden = onSale;
    if (amt && !onSale) amt.textContent = fmt(Math.round(v.price * keep / 100), (e.detail.data || {}).currency);
  });
  document.addEventListener('obt:variant-change', function (e) {
    var v = e.detail.variant, d = e.detail.data;
    document.querySelectorAll('[data-obt-recv-row]').forEach(function (tr) {
      var on = !!v && tr.dataset.obtRecvRow === String(v.id);
      tr.classList.toggle('is-selected', on);
      var tag = tr.querySelector('.obt-tag'); if (tag) tag.hidden = !on;
    });
    document.querySelectorAll('[data-obt-final-meta]').forEach(function (el) {
      var lbl = v && v.title !== 'Default Title' ? v.options.map(function (o) { return /^\d/.test(o) && /x/i.test(o) ? sizeLabel(o) : o; }).join(' / ') + ', ' : '';
      el.textContent = v ? lbl + fmt(v.price, d.currency) : 'Velg ' + ((d.optionNames || [])[0] || 'størrelse').toLowerCase();
    });
    document.querySelectorAll('[data-obt-final-btn]').forEach(function (b) {
      b.textContent = v ? 'Gå til kjøp' : 'Velg ' + ((d.optionNames || [])[0] || 'størrelse').toLowerCase();
    });
  });
  // rating link -> reviews widget (Loox renders #looxReviews; a custom anchor id wins if it exists)
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('.obt-rating');
    if (!a) return;
    var id = (a.getAttribute('href') || '').replace('#', '');
    var t = (id && document.getElementById(id)) || document.getElementById('looxReviews') || document.querySelector('.loox-widget, [id*="loox_reviews"], #judgeme_product_reviews, .jdgm-widget');
    if (!t) return;
    e.preventDefault();
    var y = t.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: y, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    t.setAttribute('tabindex', '-1'); t.focus({ preventScroll: true });
  });
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-obt-final-btn]');
    if (!b) return;
    e.preventDefault();
    var panel = document.getElementById('obt-kjop');
    if (!panel) return;
    panel.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
    var target = panel.querySelector('[data-obt-select]') || panel.querySelector('[data-obt-size-input]:checked') || panel.querySelector('[data-obt-size-input]') || panel.querySelector('[data-obt-add]');
    if (target) setTimeout(function () { target.focus({ preventScroll: true }); }, reduceMotion.matches ? 0 : 450);
  });
})();
